<template>
  <category-page
    :resource-params="resourceParams"
    :available-filters="availableFilters"
    :parent="parent"
    :filtered-resource-types="filteredResourceTypes"
    :filtered-year-levels="filteredYearLevels"
    :year-level="yearLevel"
    :resource-type="resourceType"
    :additional-path="resourceType ? `${resourceType}/` : yearLevel ? `${yearLevel}/` : undefined"
    :filtered-landing-page="filteredLandingPage"
  />
</template>

<script>
/* List resources by learning area
 *
 * @parent:
 * @requests: /public/v1/category
 * @events:
 * @props:
 * @components: CategoryPage
 * @methods:
 */

// TODO: This is almost identical to teaching-resource-collection/_.vue.  We should refactor using options API
// and pull this into a composable.  For now, if you change this file, you probably want to change
// that.

import CategoryPage from '~/components/category/CategoryPage.vue';
import { gradesForLocale } from '~/data/grades';
import { isLocale } from '~/types/locale';
import { removeLocale } from '~/utils/formatStrings';
import { longCategoryTitle, FilteredResourceTypeSlugs as resourceTypes } from '~/utils/category';

const root = 'learning-area';

export default {
  name: 'LearningArea',
  components: {
    CategoryPage,
  },
  async asyncData({ app, store, route, params, error }) {
    const data = {};
    const loggedInUser = store.getters.loggedInUser;

    const slug = params.pathMatch.split('/')[0];
    const slugQualifier = params.pathMatch.split('/')[1];

    if (slugQualifier) {
      data.filteredLandingPage = true;
    }

    // Note: This scheme assumes only either year level resource type can be a slug in first position
    const resourceTypeSlug = resourceTypes.includes(slugQualifier) ? slugQualifier : undefined;
    const yearLevelSlug =
      isLocale(app.i18n.locale) && gradesForLocale(app.i18n.locale).includes(slugQualifier)
        ? slugQualifier
        : undefined;

    // TODO: For historical reasons some resource types have -<locale> as a suffix.
    // We don't want to retain this for filtered landing pages so we have the URL
    // route include the name without the suffix and then add it when we need to
    // lookup the resources.  We will have to fix this code when we finally rename
    // the resource types so the suffix is excluded.

    if (resourceTypeSlug) {
      data.resourceType = resourceTypeSlug;
      if (app.i18n.locale !== 'au') {
        data.resourceType += '-' + app.i18n.locale;
      }
    }

    if (yearLevelSlug) {
      data.yearLevel = yearLevelSlug;
    }

    // For the resource list query
    data.resourceParams = {
      category: slug,
      perPage: 32,
      root: root + '-' + app.i18n.locale,
      resource_types: data.resourceType ? [data.resourceType] : undefined,
      year_levels: data.yearLevel ? [data.yearLevel] : loggedInUser?.year_levels,
    };

    try {
      // Load the category title, description and list of sub-categories
      const category = await store.dispatch('loadCategory', {
        category: slug,
        root: root + '-' + app.i18n.locale,
        link: route.path,
      });

      // For meta fields
      data.title = longCategoryTitle(
        category.name,
        app.i18n.locale,
        data.resourceType,
        data.yearLevel
      );
      data.category = category;
    } catch (err) {
      // No page matching this route
      console.error(err);
      error({ statusCode: 404, message: err.message, path: route.path });
    }

    return data;
  },
  data() {
    return {
      title: 'Teaching Resources by learning area',
      parent: { link: '/' + this.$i18n?.locale + '/learning-areas/', name: 'Learning Areas' },
      resourceParams: {},
      category: {},
      availableFilters: [
        'types',
        'resource_types',
        'year_levels',
        'availability',
        'file_formats',
        'publishers',
      ],
      filteredResourceTypes: [],
      filteredYearLevels: [],
      yearLevel: undefined,
      resourceType: undefined,
      filteredLandingPage: false,
    };
  },
  jsonld() {
    return this.$jsonSchemaPage(this.title, this.$route.path);
  },
  async fetch() {
    await this.getFilteredResourceTypeLinks();
  },
  head() {
    return {
      title: this.title,
      meta: this.$metaPropertiesCategory(this.category, { title: this.title }),
      link: this.$hrefLangsResource(
        this.category.linked,
        this.category.link,
        this.$i18n.locale,
        this.$route,
        this.resourceType || this.yearLevel
      ),
    };
  },
  watch: {
    //watcher to remove resource type from url the first time user uses the filters
    // excludes pagination so that it still works with resource type
    // check that there is something in query params so that redirect to remove page 1 query
    // doesn't trigger the method
    $route(to, _) {
      if (!to.query.page && Object.keys(to.query).length !== 0) {
        this.stripResourceTypeOutOfUrl(to.fullPath);
      }
    },
  },
  mounted() {
    // NB: The page event is fired in the CategoryPage component
    if (this.loggedInUser) {
      this.$mzAnalyticsApi({ event: 'pageView' });
    }
  },
  methods: {
    stripResourceTypeOutOfUrl(url) {
      const regexGetGroups = /learning-area\/(.*)\/(.*)\/(.*)/;
      if (regexGetGroups.exec(url)?.[2] === undefined) {
        return;
      }

      this.$router.push(url.replace(regexGetGroups, 'learning-area/$1/$3'));
    },
    async getFilteredResourceTypeLinks() {
      try {
        const params = { ...this.resourceParams, country: this.$i18n.locale };
        params.resource_types = [];
        params.year_levels = [];
        const response = await this.$store.dispatch('loadFilters', params);
        this.filteredResourceTypes = response.resource_types.filter(
          resourceType =>
            resourceTypes.includes(removeLocale(resourceType.slug)) && resourceType.count > 10
        );
        this.filteredYearLevels = response.year_levels.filter(yearLevel => yearLevel.count > 10);
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>
